import { Inject, Injectable } from "@angular/core";
import { HttpErrorResponse } from "@angular/common/http";
import { BehaviorSubject, Observable, catchError, map, of, tap, take } from "rxjs";
import { omit } from "lodash-es";

import { TokenFacade, TOKEN_FACADE } from '@coin-depo-core/token';

import type {
    BaseApiRequest,
    ExtendedApiResponse,
    SupportedTokens,
    TokenAllData,
    TokenPlans,
    TokenStats,
    CurrencyType,
} from '@coin-depo-types';

import { StorageService } from '@coin-depo-core/storage';
import { CurrencyService } from '@coin-depo-core/currency.service';
import { ApiService } from '@coin-depo/http';
import { AUTH_SERVICE_TOKEN, AuthFacade } from "@coin-depo-core/auth";

export interface TokenRequest extends BaseApiRequest {
    currency: CurrencyType
}

@Injectable({
    providedIn: 'root',
  })
export class TokenStatsService {
    private tokenStats$ = new BehaviorSubject<TokenStats>(
        this.storage.get('tokenStats', null)
    );
    cachedTokenStats$ = this.tokenStats$.asObservable();

    private totalInterestPaid$ = new BehaviorSubject<number>(
    this.storage.get('tokenStats.totalInterestPaid', 0)
    );
    cachedTotalInterestPaid$ = this.totalInterestPaid$.asObservable();
    private totalAccruedInterest$ = new BehaviorSubject<number>(
    this.storage.get('tokenStats.totalAccruedInterest', 0)
    );
    cachedTotalAccruedInterest$ = this.totalAccruedInterest$.asObservable();
    private totalAvailableBalance$ = new BehaviorSubject<number>(
    this.storage.get('tokenStats.total_available_balance', 0)
    );
    cachedTotalAvailableBalance$ = this.totalAvailableBalance$.asObservable();
    private totalBalance$ = new BehaviorSubject<number>(
        this.storage.get('tokenStats.total_balance', 0)
    )
    cachedTotalBalance$ =  this.totalBalance$.asObservable()


    private tokenPlans$ = new BehaviorSubject<TokenPlans[]>(
        this.storage.get('tokenPlans', null)
    )
    cachedTokenPlans$ = this.tokenPlans$.asObservable();

    private allTokensData$  = new BehaviorSubject<TokenAllData[]>(
        this.storage.get('tokenAllData', null)
    )
    cachedAllTokensData$ = this.allTokensData$.asObservable();

    private isAccessTokens$  = new BehaviorSubject<boolean>(
        this.storage.get('tokenIsAccess', true)
    )
    cachedIsAccessTokens$ = this.isAccessTokens$.asObservable();

    constructor(
        @Inject(AUTH_SERVICE_TOKEN) private auth: AuthFacade,
        private storage: StorageService,
        private tokensApi: ApiService,
        @Inject(TOKEN_FACADE) private tokenService: TokenFacade,
        private currencyService: CurrencyService
    ) {}


    updateTokensStats(currency?: CurrencyType): Observable<TokenStats> {
        const userData = this.auth.userData;

        const data: TokenRequest = {
          client_token: this.auth.accessToken,
          username: userData.email,
          currency: currency || this.currencyService.currentCurrency
        };

        return this.tokensApi.getTokenStats(data).pipe(
            catchError((err: HttpErrorResponse) => of(err.error)),
            map((res: ExtendedApiResponse<any>) => {

                if (res?.status) {
                    return res.status === 'success'
                      ? omit(res, ['parent_id', 'status', 'msg'])
                      : res.msg
                }

                return res
            }),
            tap((res: any) => {                
                if (res === null || res === undefined) {
                    this.accessTokens(false)
                }
                
                if (typeof res !== 'string') {
                    this.setTokensStates(res)
                    this.accessTokens(true)
                }
            })
        )
    }

    getTokenPlansSnapshot(): TokenPlans[] {
        return this.tokenPlans$.value;
    }

    getTokenStatsSnapshot(): TokenStats {
        return this.tokenStats$.value;
    }

    private setTokensStates(tokenStates: TokenStats) {
        this.tokenStats$.next(tokenStates);
        this.storage.set('tokenStats', tokenStates);
        this.setTotalInterestPaid(tokenStates.total_t_interest_paid_currency);
        this.setTotalAvailableBalance(tokenStates.total_t_balance_available);
        this.setTotalAccruedInterest(tokenStates.total_t_accrued_interest_currency);
        this.setTotalBalance(tokenStates?.total_balance || 0);
        this.setTokenPlans(tokenStates.plans);
        this.setAllTokensData(tokenStates.plans);
    }

    private setTotalAccruedInterest(totalAccruedInterest: number) {
        this.totalAccruedInterest$.next(totalAccruedInterest);
        this.storage.set('tokenStats.totalAccruedInterest', totalAccruedInterest);
    }
    
    private setTotalInterestPaid(totalInterestPaid: number) {
        this.totalInterestPaid$.next(totalInterestPaid);
        this.storage.set('tokenStats.totalInterestPaid', totalInterestPaid);
    }
    
    private setTotalAvailableBalance(total_available_balance: number) {
        this.totalAvailableBalance$.next(total_available_balance);
        this.storage.set(
          'tokenStats.total_available_balance',
          total_available_balance
        );
    }

    private setTotalBalance(total_balance: number) {
        this.totalBalance$.next(total_balance);
        this.storage.set('tokenStats.total_balance', total_balance);
    }

    private setTokenPlans(plans: TokenPlans[]) {
        this.tokenPlans$.next(plans);
        this.storage.set('tokenPlans', plans);
    }

    private setAllTokensData(tokenPlans: TokenPlans[]) {
        this.tokenService.cachedSupportedTokens$
        .pipe(take(1))
        .subscribe((supportedTokens: SupportedTokens[]) => {
                // @ts-ignore  
            const allDataTokens: TokenAllData[] = supportedTokens.map((token: SupportedTokens) => {
                const tokenFromPlans = tokenPlans.find((plan: TokenPlans) => plan.coin_id === token.coin_id);
                return ({
                    ...tokenFromPlans,
                    ...token,
                })
            });

            this.storage.set('tokenAllData', allDataTokens);
            this.allTokensData$.next(allDataTokens);
        });
    }

    private accessTokens(isDisable: boolean) {
        this.isAccessTokens$.next(isDisable);
        this.storage.set('tokenIsAccess', isDisable);
    }
}
